import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import VoluntarySurveyService from "../../services/voluntary-surveys/voluntarysurvey.services";

export type VoluntarySurveysListFetchStatus = "idle" | "loading" | "succeeded" | "failed";

/** Dedupe concurrent in-flight fetches for `ensureAllVoluntarySurveysLoaded`. */
let voluntarySurveysListInflight: Promise<any[]> | null = null;

type VoluntarySurveyBranch = {
  listFetchStatus: VoluntarySurveysListFetchStatus;
  allVoluntarySurveysList: any[];
};

type ThunkGetState = () => { VoluntarySurvey: VoluntarySurveyBranch };

/**
 * Loads the full voluntary surveys list once (or joins an in-flight request).
 * Skips the network when data is already in the store as `succeeded` (see `condition`).
 */
export const ensureAllVoluntarySurveysLoaded = createAsyncThunk(
  "VoluntarySurvey/ensureAllLoaded",
  async (_, { getState }) => {
    if (voluntarySurveysListInflight) {
      return voluntarySurveysListInflight;
    }
    voluntarySurveysListInflight = (async () => {
      const data = await VoluntarySurveyService.getAllVoluntarySurveysList();
      return Array.isArray(data) ? data : [];
    })();
    try {
      return await voluntarySurveysListInflight;
    } finally {
      voluntarySurveysListInflight = null;
    }
  },
  {
    condition: (_, { getState }) => {
      const status = (getState() as ReturnType<ThunkGetState>).VoluntarySurvey.listFetchStatus;
      return status !== "succeeded";
    },
  }
);

/** Always refetches (e.g. after pin/unpin) and replaces store data. */
export const refreshAllVoluntarySurveysList = createAsyncThunk(
  "VoluntarySurvey/refreshAll",
  async () => {
    const data = await VoluntarySurveyService.getAllVoluntarySurveysList();
    return Array.isArray(data) ? data : [];
  }
);

const VoluntarySurveySlice = createSlice({
  name: "VoluntarySurveySlice",
  initialState: {
    siteInfo: [] as any[],
    allVoluntarySurveysList: [] as any[],
    listFetchStatus: "idle" as VoluntarySurveysListFetchStatus,
  },
  reducers: {
    SiteInfoReducer: (state: any, action: any) => {
      state.siteInfo = action.payload;
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ensureAllVoluntarySurveysLoaded.pending, (state) => {
        if (state.listFetchStatus !== "succeeded") {
          state.listFetchStatus = "loading";
        }
      })
      .addCase(ensureAllVoluntarySurveysLoaded.fulfilled, (state, action) => {
        state.allVoluntarySurveysList = action.payload;
        state.listFetchStatus = "succeeded";
      })
      .addCase(ensureAllVoluntarySurveysLoaded.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.listFetchStatus = "failed";
        state.allVoluntarySurveysList = [];
      })
      .addCase(refreshAllVoluntarySurveysList.pending, (state) => {
        state.listFetchStatus = "loading";
      })
      .addCase(refreshAllVoluntarySurveysList.fulfilled, (state, action) => {
        state.allVoluntarySurveysList = action.payload;
        state.listFetchStatus = "succeeded";
      })
      .addCase(refreshAllVoluntarySurveysList.rejected, (state) => {
        state.listFetchStatus = "failed";
      });
  },
});

export const { SiteInfoReducer } = VoluntarySurveySlice.actions;

export default VoluntarySurveySlice;
